import { eventTracker } from 'app/modules/Segment';
import {
  FeatureEnum,
  GroupIdEnum,
  OwnerEnum,
  TContext,
  TProperties,
} from 'app/modules/Segment/types';

const EVENTS = {
  JUS_ZAP_UPSELL_MODAL_VIEWED: 'jus_zap_upsell_modal_viewed',
  JUS_ZAP_UPGRADE_BUTTON_CLICKED: 'jus_zap_upgrade_button_clicked',
  JUS_ZAP_WHATSAPP_CONNECTED_SUCCESSFULLY:
    'jus_zap_whatsapp_connected_successfully',
  JUS_ZAP_CUSTOM_FLOW_OPENED: 'jus_zap_custom_flow_opened',
  JUS_ZAP_CUSTOM_AI_ONBOARDING_STARTED:
    'jus_zap_custom_ai_onboarding_started',
  JUS_ZAP_CUSTOM_AI_ONBOARDING_STEP_VIEWED:
    'jus_zap_custom_ai_onboarding_step_viewed',
  JUS_ZAP_CUSTOM_AI_ONBOARDING_SUBMITTED:
    'jus_zap_custom_ai_onboarding_submitted',
  JUS_ZAP_CUSTOM_AI_FLOW_OPENED: 'jus_zap_custom_ai_flow_opened',
  JUS_ZAP_CUSTOM_AI_NEW_FLOW_REQUESTED:
    'jus_zap_custom_ai_new_flow_requested',
  JUS_ZAP_CUSTOM_AI_FLOW_DRAFT_GENERATED:
    'jus_zap_custom_ai_flow_draft_generated',
  JUS_ZAP_CUSTOM_AI_FLOW_SAVED: 'jus_zap_custom_ai_flow_saved',
  JUS_ZAP_CUSTOM_AI_ALL_FLOWS_COMPLETED:
    'jus_zap_custom_ai_all_flows_completed',
  JUS_ZAP_CUSTOM_AI_FAQ_EXPANDED: 'jus_zap_custom_ai_faq_expanded',
  JUS_ZAP_BANNER_VIEWED: 'jus_zap_banner_viewed',
  JUS_ZAP_BANNER_BUTTON_CLICKED: 'jus_zap_banner_button_clicked',
  JUS_ZAP_RESPOND_BUTTON_CLICKED: 'jus_zap_respond_button_clicked',
  JUS_ZAP_CONNECT_WHATSAPP_BUTTON_CLICKED:
    'jus_zap_connect_whatsapp_button_clicked',
  JUS_ZAP_CONNECT_WHATSAPP: 'jus_zap_connect_whatsapp',
  JUS_ZAP_RENEW_PLAN_BUTTON_CLICKED: 'jus_zap_renew_plan_button_clicked',
  JUS_ZAP_CARD_RENEWAL_CREATED: 'jus_zap_card_renewal_created',
  JUS_ZAP_CARD_RENEWAL_ERROR: 'jus_zap_card_renewal_error',
  JUS_ZAP_UPDATE_INSTANCE_STARTED: 'jus_zap_update_instance_started',
  JUS_ZAP_UPDATE_INSTANCE_SUCCESS: 'jus_zap_update_instance_success',
  JUS_ZAP_UPDATE_INSTANCE_ERROR: 'jus_zap_update_instance_error',
  JUS_ZAP_RECREATE_INSTANCE_STARTED: 'jus_zap_recreate_instance_started',
  JUS_ZAP_RECREATE_INSTANCE_SUCCESS: 'jus_zap_recreate_instance_success',
  JUS_ZAP_RECREATE_INSTANCE_ERROR: 'jus_zap_recreate_instance_error',
  JUS_ZAP_HELP_CLICKED: 'jus_zap_help_clicked',
  JUS_ZAP_PAGE_VIEWED: 'jus_zap_page_viewed',
  JUS_ZAP_PAGE_FAQ_OPENED: 'jus_zap_page_faq_opened',
  JUS_ZAP_PAGE_VIDEO_PLAYED: 'jus_zap_page_video_played',
  JUS_ZAP_PAGE_ACTIVATE_BUTTON_CLICKED: 'jus_zap_page_activate_button_clicked',
  JUS_ZAP_PAGE_CONFIGURATION_CREATED: 'jus_zap_page_configuration_created',
  JUS_ZAP_PAGE_CONFIGURATION_ERROR: 'jus_zap_page_configuration_error',
  JUS_ZAP_PAGE_SCROLL_TO_TOP_CLICKED: 'jus_zap_page_scroll_to_top_clicked',
  JUS_ZAP_QR_CODE_RETRY_CLICKED: 'jus_zap_qr_code_retry_clicked',
  JUS_ZAP_QR_CODE_REGENERATE_CLICKED: 'jus_zap_qr_code_regenerate_clicked',
} as const;

const JUSZAP_LIBRARY_NAME = 'juszap-library';
const JUSZAP_FEATURE = FeatureEnum.JUSZAP;

function buildBaseProperties(
  eventText: string,
  relevantUserActivity: boolean,
  eventMeaning: string,
  overrides: Partial<TProperties> = {},
): TProperties {
  return {
    owner: OwnerEnum.INOVACAO,
    feature: JUSZAP_FEATURE,
    event_meaning: eventMeaning,
    relevant_user_activity: relevantUserActivity,
    event_text: eventText,
    ...overrides,
  } as TProperties;
}

function buildContext(componentName: string): TContext {
  return {
    groupId: GroupIdEnum.JUSZAP_GROUP,
    library: {
      name: JUSZAP_LIBRARY_NAME,
      componente: componentName,
    },
  };
}

export function trackJusZapUpsellModalViewed(props: {
  context?: string;
}): void {
  const event = EVENTS.JUS_ZAP_UPSELL_MODAL_VIEWED;
  const properties = buildBaseProperties(
    'JusZap Upsell Modal Viewed',
    false,
    'Disparado quando o modal de upsell do JusZap é exibido ao usuário sem plano custom. Propriedades: `context`.',
    {
      context: props.context || 'unknown',
    },
  );

  eventTracker.track({
    event,
    properties,
    context: buildContext('modal-component'),
  });
}

export function trackJusZapUpgradeButtonClicked(props: {
  context?: string;
}): void {
  const event = EVENTS.JUS_ZAP_UPGRADE_BUTTON_CLICKED;
  const properties = buildBaseProperties(
    'JusZap Upgrade Button Clicked',
    true,
    'Disparado quando o usuário clica no botão de upgrade do plano JusZap. Propriedades: `context`.',
    {
      context: props.context || 'unknown',
    },
  );

  eventTracker.track({
    event,
    properties,
    context: buildContext('button-component'),
  });
}

export function trackJusZapCustomFlowOpened(props: {
  context?: string;
}): void {
  const event = EVENTS.JUS_ZAP_CUSTOM_FLOW_OPENED;
  const properties = buildBaseProperties(
    'JusZap Custom Flow Opened',
    true,
    'Disparado quando o usuário do plano custom abre a tela de configuração de fluxo personalizado do JusZap. Propriedades: `context`.',
    {
      context: props.context || 'unknown',
    },
  );

  eventTracker.track({
    event,
    properties,
    context: buildContext('custom-flow-component'),
  });
}

export function trackJusZapWhatsappConnectedSuccessfully(props: {
  context?: string;
}): void {
  const event = EVENTS.JUS_ZAP_WHATSAPP_CONNECTED_SUCCESSFULLY;
  const properties = buildBaseProperties(
    'JusZap Whatsapp Connected Successfully',
    true,
    'Disparado quando a conexão do WhatsApp via QR Code do JusZap é concluída com sucesso. Propriedades: `context`, `status`.',
    {
      context: props.context || 'qr_code_scan',
      status: 'success',
    },
  );

  eventTracker.track({
    event,
    properties,
    context: buildContext('qrcode-component'),
  });
}

export function trackJusZapCustomAiOnboardingStarted(props: {
  context?: string;
} = {}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CUSTOM_AI_ONBOARDING_STARTED,
    properties: buildBaseProperties(
      'JusZap Custom AI Onboarding Started',
      true,
      'Disparado quando o usuário clica em "Personalizar agora" no empty state da personalização de IA do JusZap. Propriedades: `context`.',
      {
        context: props.context || 'empty_state',
      },
    ),
    context: buildContext('empty-state-component'),
  });
}

export function trackJusZapCustomAiOnboardingStepViewed(props: {
  step: number;
  totalSteps?: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CUSTOM_AI_ONBOARDING_STEP_VIEWED,
    properties: buildBaseProperties(
      'JusZap Custom AI Onboarding Step Viewed',
      true,
      'Disparado a cada vez que o usuário visualiza um passo do onboarding da personalização da IA do JusZap. Propriedades: `step`, `total_steps`.',
      {
        step: props.step,
        total_steps: props.totalSteps ?? null,
      },
    ),
    context: buildContext('onboarding-modal-component'),
  });
}

export function trackJusZapCustomAiOnboardingSubmitted(props: {
  legalAreasCount: number;
  expectationsCount: number;
  sensitiveTopicsCount: number;
  addressing: string | null;
  identificationMode: string | null;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CUSTOM_AI_ONBOARDING_SUBMITTED,
    properties: buildBaseProperties(
      'JusZap Custom AI Onboarding Submitted',
      true,
      'Disparado quando o onboarding da personalização da IA é submetido com sucesso ao backend. Propriedades: `legal_areas_count`, `expectations_count`, `sensitive_topics_count`, `addressing`, `identification_mode`.',
      {
        legal_areas_count: props.legalAreasCount,
        expectations_count: props.expectationsCount,
        sensitive_topics_count: props.sensitiveTopicsCount,
        addressing: props.addressing ?? null,
        identification_mode: props.identificationMode ?? null,
      },
    ),
    context: buildContext('onboarding-modal-component'),
  });
}

export function trackJusZapCustomAiFlowOpened(props: {
  flowId: string | null;
  flowStatus: string | null;
  flowTitle?: string | null;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CUSTOM_AI_FLOW_OPENED,
    properties: buildBaseProperties(
      'JusZap Custom AI Flow Opened',
      true,
      'Disparado quando o usuário abre um fluxo existente na lista de fluxos da personalização da IA. Propriedades: `flow_id`, `flow_status`, `flow_title`.',
      {
        flow_id: props.flowId ?? null,
        flow_status: props.flowStatus ?? null,
        flow_title: props.flowTitle ?? null,
      },
    ),
    context: buildContext('flow-card-component'),
  });
}

export function trackJusZapCustomAiNewFlowRequested(props: {
  currentFlowsCount: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CUSTOM_AI_NEW_FLOW_REQUESTED,
    properties: buildBaseProperties(
      'JusZap Custom AI New Flow Requested',
      true,
      'Disparado quando o usuário clica em "Novo fluxo" na lista de fluxos da personalização da IA. Propriedades: `current_flows_count`.',
      {
        current_flows_count: props.currentFlowsCount,
      },
    ),
    context: buildContext('button-component'),
  });
}

export function trackJusZapCustomAiFlowDraftGenerated(props: {
  titleLength: number;
  descriptionLength: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CUSTOM_AI_FLOW_DRAFT_GENERATED,
    properties: buildBaseProperties(
      'JusZap Custom AI Flow Draft Generated',
      true,
      'Disparado quando o backend retorna com sucesso um rascunho de fluxo gerado por IA a partir do título/descrição fornecidos pelo usuário. Propriedades: `title_length`, `description_length`.',
      {
        title_length: props.titleLength,
        description_length: props.descriptionLength,
      },
    ),
    context: buildContext('new-flow-modal-component'),
  });
}

export function trackJusZapCustomAiFlowSaved(props: {
  flowId: string | null;
  flowStatus: string;
  isNewFlow: boolean;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CUSTOM_AI_FLOW_SAVED,
    properties: buildBaseProperties(
      'JusZap Custom AI Flow Saved',
      true,
      'Disparado quando um fluxo personalizado é salvo com sucesso (criação ou atualização). Propriedades: `flow_id`, `flow_status`, `is_new_flow`.',
      {
        flow_id: props.flowId ?? null,
        flow_status: props.flowStatus,
        is_new_flow: props.isNewFlow,
      },
    ),
    context: buildContext('flow-editor-component'),
  });
}

export function trackJusZapCustomAiAllFlowsCompleted(props: {
  flowsCount: number;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CUSTOM_AI_ALL_FLOWS_COMPLETED,
    properties: buildBaseProperties(
      'JusZap Custom AI All Flows Completed',
      true,
      'Disparado quando todos os fluxos da personalização da IA foram concluídos pelo usuário e o status global passou para "aguardando revisão". Propriedades: `flows_count`.',
      {
        flows_count: props.flowsCount,
      },
    ),
    context: buildContext('flow-editor-component'),
  });
}

export function trackJusZapCustomAiFaqExpanded(props: {
  faqId: string;
  faqQuestion: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CUSTOM_AI_FAQ_EXPANDED,
    properties: buildBaseProperties(
      'JusZap Custom AI FAQ Expanded',
      false,
      'Disparado quando o usuário expande uma pergunta do FAQ na tela de personalização da IA. Propriedades: `faq_id`, `faq_question`.',
      {
        faq_id: props.faqId,
        faq_question: props.faqQuestion,
      },
    ),
    context: buildContext('faq-component'),
  });
}

export function trackJusZapBannerViewed(): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_BANNER_VIEWED,
    properties: buildBaseProperties(
      'JusZap Banner Viewed',
      false,
      'Disparado quando o card de apresentação do JusZap é exibido ao usuário no dashboard.',
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapBannerButtonClicked(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_BANNER_BUTTON_CLICKED,
    properties: buildBaseProperties(
      'JusZap Banner Button Clicked',
      true,
      'Disparado quando o usuário clica no botão "Ativar agora" no card de apresentação do JusZap no dashboard. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapRespondButtonClicked(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_RESPOND_BUTTON_CLICKED,
    properties: buildBaseProperties(
      'JusZap Respond Button Clicked',
      true,
      'Disparado quando o usuário clica em "Responder agora" no card do JusZap no dashboard. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapConnectWhatsAppButtonClicked(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CONNECT_WHATSAPP_BUTTON_CLICKED,
    properties: buildBaseProperties(
      'JusZap Connect WhatsApp Button Clicked',
      true,
      'Disparado quando o usuário clica no botão de conexão do WhatsApp no card de conexão pendente do dashboard. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapConnectWhatsApp(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CONNECT_WHATSAPP,
    properties: buildBaseProperties(
      'JusZap Connect WhatsApp',
      true,
      'Disparado quando o usuário navega para a tela de conexão do WhatsApp a partir do dashboard. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapRenewPlanButtonClicked(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_RENEW_PLAN_BUTTON_CLICKED,
    properties: buildBaseProperties(
      'JusZap Renew Plan Button Clicked',
      true,
      'Disparado quando o usuário clica em "Renovar agora" no card de plano expirado do JusZap no dashboard. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapCardRenewalCreated(props: {
  businessName: string;
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CARD_RENEWAL_CREATED,
    properties: buildBaseProperties(
      'JusZap Card Renewal Created',
      false,
      'Disparado quando a renovação do plano JusZap via cartão é processada com sucesso no dashboard. Propriedades: `business_name`, `context`.',
      {
        business_name: props.businessName,
        context: props.context,
      },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapCardRenewalError(props: {
  errorMessage: string;
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_CARD_RENEWAL_ERROR,
    properties: buildBaseProperties(
      'JusZap Card Renewal Error',
      false,
      'Disparado quando o processamento de renovação do plano JusZap falha no dashboard. Propriedades: `error_message`, `context`.',
      {
        error_message: props.errorMessage,
        context: props.context,
      },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapUpdateInstanceStarted(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_UPDATE_INSTANCE_STARTED,
    properties: buildBaseProperties(
      'JusZap Update Instance Started',
      true,
      'Disparado quando o usuário inicia a atualização da instância Evolution do JusZap pelo dashboard. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapUpdateInstanceSuccess(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_UPDATE_INSTANCE_SUCCESS,
    properties: buildBaseProperties(
      'JusZap Update Instance Success',
      false,
      'Disparado quando a atualização da instância Evolution do JusZap é concluída com sucesso. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapUpdateInstanceError(props: {
  error: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_UPDATE_INSTANCE_ERROR,
    properties: buildBaseProperties(
      'JusZap Update Instance Error',
      false,
      'Disparado quando a atualização da instância Evolution do JusZap falha. Propriedades: `error`.',
      { error: props.error },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapRecreateInstanceStarted(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_RECREATE_INSTANCE_STARTED,
    properties: buildBaseProperties(
      'JusZap Recreate Instance Started',
      true,
      'Disparado quando o usuário inicia a recriação da configuração inativa do JusZap pelo dashboard. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapRecreateInstanceSuccess(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_RECREATE_INSTANCE_SUCCESS,
    properties: buildBaseProperties(
      'JusZap Recreate Instance Success',
      false,
      'Disparado quando a recriação da configuração do JusZap é concluída com sucesso. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapRecreateInstanceError(props: {
  error: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_RECREATE_INSTANCE_ERROR,
    properties: buildBaseProperties(
      'JusZap Recreate Instance Error',
      false,
      'Disparado quando a recriação da configuração do JusZap falha. Propriedades: `error`.',
      { error: props.error },
    ),
    context: buildContext('juszap-card-component'),
  });
}

export function trackJusZapHelpClicked(): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_HELP_CLICKED,
    properties: buildBaseProperties(
      'JusZap Help Clicked',
      true,
      'Disparado quando o usuário clica no botão de ajuda na sidebar do JusZap.',
    ),
    context: buildContext('chat-sidebar'),
  });
}

export function trackJusZapPageViewed(props: {
  context?: string;
} = {}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_PAGE_VIEWED,
    properties: buildBaseProperties(
      'JusZap Page Viewed',
      false,
      'Disparado quando a página de apresentação do JusZap é exibida ao usuário. Propriedades: `context`.',
      { context: props.context ?? null },
    ),
    context: buildContext('juszap-page'),
  });
}

export function trackJusZapPageFaqOpened(props: {
  faqId: string | number;
  faqQuestion: string;
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_PAGE_FAQ_OPENED,
    properties: buildBaseProperties(
      'JusZap Page FAQ Opened',
      true,
      'Disparado quando o usuário expande uma pergunta do FAQ na página de apresentação do JusZap. Propriedades: `faq_id`, `faq_question`, `context`.',
      {
        faq_id: props.faqId,
        faq_question: props.faqQuestion,
        context: props.context,
      },
    ),
    context: buildContext('juszap-page'),
  });
}

export function trackJusZapPageVideoPlayed(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_PAGE_VIDEO_PLAYED,
    properties: buildBaseProperties(
      'JusZap Page Video Played',
      true,
      'Disparado quando o usuário reproduz o vídeo na página de apresentação do JusZap. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-page'),
  });
}

export function trackJusZapPageActivateButtonClicked(props: {
  businessName: string;
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_PAGE_ACTIVATE_BUTTON_CLICKED,
    properties: buildBaseProperties(
      'JusZap Page Activate Button Clicked',
      true,
      'Disparado quando o usuário clica no botão de ativação na página de apresentação do JusZap. Propriedades: `business_name`, `context`.',
      {
        business_name: props.businessName,
        context: props.context,
      },
    ),
    context: buildContext('juszap-page'),
  });
}

export function trackJusZapPageConfigurationCreated(props: {
  businessName: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_PAGE_CONFIGURATION_CREATED,
    properties: buildBaseProperties(
      'JusZap Page Configuration Created',
      false,
      'Disparado quando a configuração do JusZap é criada com sucesso via página de apresentação. Propriedades: `business_name`.',
      { business_name: props.businessName },
    ),
    context: buildContext('juszap-page'),
  });
}

export function trackJusZapPageConfigurationError(props: {
  errorMessage: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_PAGE_CONFIGURATION_ERROR,
    properties: buildBaseProperties(
      'JusZap Page Configuration Error',
      false,
      'Disparado quando a criação da configuração do JusZap falha na página de apresentação. Propriedades: `error_message`.',
      { error_message: props.errorMessage },
    ),
    context: buildContext('juszap-page'),
  });
}

export function trackJusZapPageScrollToTopClicked(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_PAGE_SCROLL_TO_TOP_CLICKED,
    properties: buildBaseProperties(
      'JusZap Page Scroll To Top Clicked',
      true,
      'Disparado quando o usuário clica em um botão de CTA que rola a página ao topo na página de apresentação do JusZap. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('juszap-page'),
  });
}

export function trackJusZapQrCodeRetryClicked(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_QR_CODE_RETRY_CLICKED,
    properties: buildBaseProperties(
      'JusZap QR Code Retry Clicked',
      true,
      'Disparado quando o usuário clica em "Tentar Novamente" após falha na geração do QR Code do WhatsApp. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('qrcode-component'),
  });
}

export function trackJusZapQrCodeRegenerateClicked(props: {
  context: string;
}): void {
  eventTracker.track({
    event: EVENTS.JUS_ZAP_QR_CODE_REGENERATE_CLICKED,
    properties: buildBaseProperties(
      'JusZap QR Code Regenerate Clicked',
      true,
      'Disparado quando o usuário clica em "Regerar QR Code" manualmente na tela de conexão do WhatsApp. Propriedades: `context`.',
      { context: props.context },
    ),
    context: buildContext('qrcode-component'),
  });
}
